









































































































































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {v4 as uuidv4} from "uuid";
import FscSimpleCard from "@/components/Card/FscSimpleCard.vue";
import EditButton from "@/components/Button/EditButton.vue";
import ActionButton from "@/components/Button/ActionButton.vue";
import RemoveButton from "@/components/Button/RemoveButton.vue";
import AbortButton from "@/components/Button/AbortButton.vue";
import SaveButton from "@/components/Button/SaveButton.vue";
import ArchiveModal from "@/components/Modal/ArchiveModal.vue";
import FscModal from "@/components/Modal/FscModal.vue";
import ModalMixin from "@/mixins/ModalMixin";
import {mixins} from "vue-class-component";
import {IDrivingLessonTaskGroup} from "@/interfaces/IDrivingLessonTaskGroup";
import {IDrivingLessonTask} from "@/interfaces/IDrivingLessonTask";
import {IBasicNamedDTO} from "@/interfaces/IBasicNamedDTO";
import {ILicenseClassGroup} from "@/interfaces/ILicenseClassGroup";
import {GFA_GROUP_MANAGEMENT_ENABLED} from "@/constants/DrivingLessonTaskGroup";

const DrivingLessonTaskGroupModule = namespace("driving-lesson-task-group");

const DEFAULT_TASK_ICON = "road";

/** Icon slugs registered in src/icons/Solid.ts for GFA tasks. Some icons from the source CSV
 * (arrows-split-up-and-left, u-turn-left-down, bus-stop) aren't available in the currently
 * installed FontAwesome package version, so they fall back to the default road icon here; the
 * backend keeps storing the exact CSV slug for consumers (e.g. the mobile app) that can render it. */
const AVAILABLE_TASK_ICONS = new Set([
  DEFAULT_TASK_ICON,
  "traffic-cone",
  "brake-warning",
  "hand",
  "arrow-rotate-right",
  "turn-down-right",
  "turn-down-left",
  "square-parking",
  "alt",
  "arrow-up-to-line",
  "arrow-up",
  "arrow-down",
]);

@Component({
  components: {
    FscSimpleCard,
    EditButton,
    ActionButton,
    RemoveButton,
    AbortButton,
    SaveButton,
    ArchiveModal,
    FscModal,
  },
})
export default class DrivingLessonTaskGroupCard extends mixins(ModalMixin) {
  public name = "DrivingLessonTaskGroupCard";

  public gfaGroupManagementEnabled = GFA_GROUP_MANAGEMENT_ENABLED;

  @Prop({required: true})
  public group!: IDrivingLessonTaskGroup;

  @Prop({type: Array, default: () => []})
  public licenseClassOptions!: Array<{ text: string; value: number }>;

  @Prop({type: Array, default: () => []})
  public licenseClassGroups!: Array<ILicenseClassGroup>;

  @DrivingLessonTaskGroupModule.Action("saveGroup")
  public saveGroupAction: any;

  @DrivingLessonTaskGroupModule.Action("findGroupById")
  public findGroupByIdAction: any;

  @DrivingLessonTaskGroupModule.Action("archive")
  public archiveGroupAction: any;

  @DrivingLessonTaskGroupModule.Action("revertToDefault")
  public revertToDefaultAction: any;

  @DrivingLessonTaskGroupModule.Getter("getArchiveSuccess")
  public archiveSuccess: any;

  public editing = false;

  public saving = false;

  public draggedIndex: number | null = null;

  public dragOverIndex: number | null = null;

  public localGroup: { name: string; licenseClassIds: Array<number>; tasks: Array<any> } = {
    name: "",
    licenseClassIds: [],
    tasks: [],
  };

  public licenseClassModalSelection: Array<number> = [];

  public licenseClassSelectionRequired = false;

  public get archiveModalId(): string {
    return `driving-lesson-task-group-archive-${this.group.id}`;
  }

  public get revertToDefaultModalId(): string {
    return `driving-lesson-task-group-revert-to-default-${this.group.id}`;
  }

  public get licenseClassModalId(): string {
    return `driving-lesson-task-group-license-classes-${this.group.id}`;
  }

  public get selectedLicenseClassNames(): string {
    return this.licenseClassOptions
        .filter((option) => this.localGroup.licenseClassIds.includes(option.value))
        .map((option) => option.text)
        .join(", ");
  }

  public toOptions(licenseClasses: Array<IBasicNamedDTO>): Array<{ text: string; value: number }> {
    return licenseClasses.map((licenseClass) => ({
      text: licenseClass.name as string,
      value: licenseClass.id as number
    }));
  }

  public resolveTaskIcon(icon?: string): string {
    return icon && AVAILABLE_TASK_ICONS.has(icon) ? icon : DEFAULT_TASK_ICON;
  }

  public get visibleTasks(): Array<any> {
    return this.editing ? this.localGroup.tasks : this.group.tasks;
  }

  public startEdit(): void {
    this.localGroup = {
      name: this.group.name || "",
      licenseClassIds: this.group.licenseClasses.map((licenseClass) => licenseClass.id as number),
      tasks: this.group.tasks.map((task: IDrivingLessonTask) => ({...task})),
    };
    this.editing = true;
  }

  public cancelEdit(): void {
    this.editing = false;
  }

  public openLicenseClassModal(): void {
    this.licenseClassModalSelection = [...this.localGroup.licenseClassIds];
    this.licenseClassSelectionRequired = false;
    this.showModal(this.licenseClassModalId);
  }

  public applyLicenseClassSelection(): void {
    if (this.licenseClassModalSelection.length === 0) {
      this.licenseClassSelectionRequired = true;
      return;
    }
    this.localGroup.licenseClassIds = [...this.licenseClassModalSelection];
    this.hideModal(this.licenseClassModalId);
  }

  public cancelLicenseClassModal(): void {
    this.hideModal(this.licenseClassModalId);
  }

  public addTask(): void {
    const newTaskKey = uuidv4();
    this.localGroup.tasks.push({
      id: null,
      text: "",
      enabled: true,
      orderIndex: this.localGroup.tasks.length + 1,
      icon: DEFAULT_TASK_ICON,
      _key: newTaskKey,
    });

    this.$nextTick(() => {
      const scrollContainer = this.$el.querySelector(".fsc-scroll");
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }

      // a ref inside v-for is always wrapped in an array by Vue, even with a unique key per iteration
      const refs = this.$refs[newTaskKey] as Vue | Array<Vue> | undefined;
      const newTaskInput = Array.isArray(refs) ? refs[0] : refs;
      (newTaskInput as any)?.focus?.();
    });
  }

  public removeTask(index: number): void {
    this.localGroup.tasks.splice(index, 1);
  }

  public onDragStart(index: number): void {
    this.draggedIndex = index;
  }

  public onDragOver(index: number): void {
    this.dragOverIndex = index;
  }

  public onDragEnd(): void {
    this.draggedIndex = null;
    this.dragOverIndex = null;
  }

  public onDrop(targetIndex: number): void {
    if (this.draggedIndex === null || this.draggedIndex === targetIndex) {
      this.onDragEnd();
      return;
    }

    const tasks = this.localGroup.tasks;
    const [movedTask] = tasks.splice(this.draggedIndex, 1);
    tasks.splice(targetIndex, 0, movedTask);
    this.onDragEnd();
  }

  public async save(): Promise<void> {
    const oldId = this.group.id;
    const data = {
      id: oldId,
      name: this.localGroup.name ? this.localGroup.name.trim() : null,
      licenseClasses: this.localGroup.licenseClassIds.map((id: number) => ({id})),
      tasks: this.localGroup.tasks.map((task, index) => ({
        id: task.id,
        text: task.text,
        enabled: task.enabled,
        orderIndex: index + 1,
        icon: task.icon || DEFAULT_TASK_ICON,
      })),
    };

    this.saving = true;
    try {
      const putResponse = await this.saveGroupAction({id: oldId, data, resource: "driving-lesson-task-groups"});
      const effectiveId = putResponse.data;
      const getResponse = await this.findGroupByIdAction({id: effectiveId, resource: "driving-lesson-task-groups"});

      this.editing = false;
      this.$emit("saved", {oldId, group: getResponse.data});
    } finally {
      this.saving = false;
    }
  }

  public showRevertToDefaultModal(): void {
    this.showModal(this.revertToDefaultModalId);
  }

  public async confirmRevertToDefault(): Promise<void> {
    await this.revertToDefaultAction({id: this.group.id, resource: "driving-lesson-task-groups"});
    this.$emit("reverted");
  }

  public showArchiveModal(): void {
    this.showModal(this.archiveModalId);
  }

  @Watch("archiveSuccess")
  public onArchiveSuccess(success: any): void {
    if (success) {
      this.editing = false;
      this.$emit("archived");
    }
  }
}
